import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db/db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  DateTimeProvider,
  Id,
  createObj,
  enumGetValues,
  runIfDefined,
  throwAccessDenied,
  throwInternalError,
  throwInvalidParameter,
  throwObjectNotFound,
  trCore,
  validateRequiredId,
  validateRequiredNumber,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { Permissions } from '../../authorization'
import {
  IdentityCard,
  IdentityCardsService,
  IdentityCardsRepository,
  PrintableDataForIdentityCard,
  ConfirmableDataForIdentityCard,
} from './identityCards'
import { Citizen, CitizensRepository } from '../../citizens'
import { UsersRepository, User } from '../../users'
import { GLOBAL_SETTINGS_ID, GlobalSettings, GlobalSettingsRepository } from '../../globalSettings'
import { CardIssuePoint, CardIssuePointsRepository } from '../../cardIssuePoints'

enum EventType {
  IDENTITY_CARD_ADD = 'IDENTITY_CARD_ADD',
  IDENTITY_CARD_UPDATE = 'IDENTITY_CARD_UPDATE',
  IDENTITY_CARD_MERGE = 'IDENTITY_CARD_MERGE',
}

type AddIdentityCardPayload = {
  month: number
  year: number
  citizenId: Id
  cardIssuePointId?: Id
  confirmedAt?: string
}

type UpdateIdentityCardPayload = {
  cardIssuePointId?: Id
  confirmedAt?: string
}

export type AddIdentityCardOpts = {
  municipalEntityId: Id
  month: number
  year: number
  cardIssuePointId?: Id
  citizenId: Id
  confirmedAt?: Date
}

export type UpdateIdentityCardOpts = {
  municipalEntityId: Id
  cardIssuePointId?: Id
  confirmedAt?: Date
}

type MergeIdentityCardPayload = {
  cardIssuePointId?: Id
  citizenId: Id
  confirmedAt?: string
  createdAt: string
  month: number
  year: number
}

interface DBIdentityCardsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  identityCardsRepository: IdentityCardsRepository
  usersRepository: UsersRepository
  citizensRepository: CitizensRepository
  dateTimeProvider: DateTimeProvider
  globalSettingsRepository: GlobalSettingsRepository
  cardIssuePointsRepository: CardIssuePointsRepository
}

export class DBIdentityCardsService
  extends BaseDBService<DBIdentityCardsServiceOpts, CoreDict>
  implements IdentityCardsService
{
  private validateMonth = (month?: number) =>
    validateRequiredNumber('<ad916e70> Month', month, { minValue: 0, maxValue: 11 })
  private validateYear = (year?: number) => validateRequiredNumber('<fde1945b> Year', year, { minValue: 2022 })
  private validateCitizenID = (citizenID?: Id) => validateRequiredId('<47b72190> Citizen ID', citizenID)
  private validateUserID = (userID?: Id) => validateRequiredId('<6c7e4f60> User ID', userID)

  constructor(opts: DBIdentityCardsServiceOpts) {
    super('DBIdentityCardsService', opts)
    this.logger.debug({}, '<841e4b37> Init')
  }

  async findCitizenCards(ctx: Context, citizenId: string): Promise<IdentityCard[]> {
    let citizen: Citizen | undefined
    let globalSettings: GlobalSettings | undefined
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      this.validateCitizenID(citizenId)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.IDENTITY_CARDS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_IDENTITY_CARDS_VIEW)

        citizen = await this.checkForCitizenExists('<48e22353>', ctx, tx, citizenId)
        await this.checkAccessToCitizen('<423fe1b6>', ctx, tx, citizen)
      }

      citizen ||= await this.checkForCitizenExists('<1a7334cf>', ctx, tx, citizenId)
      globalSettings = await this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID)
    })

    const cards = await runWithROTransaction(
      this.opts.dbConnection,
      async tx => await this.opts.identityCardsRepository.findAll(tx, { filter: { citizenIds: [citizenId] } }),
    )

    // Для отображения создаём фейковые карты, которые гражданин не получал
    const citizenCreatedAt = citizen?.createdAt ? new Date(citizen.createdAt) : new Date(2022, 0, 1)
    let iterMonth = citizenCreatedAt.getMonth()
    let iterYear = citizenCreatedAt.getFullYear()
    const currentDateTime = this.opts.dateTimeProvider.get()
    const endYear = currentDateTime.getFullYear()
    const endMonth = currentDateTime.getMonth()

    while (iterYear < endYear || (iterYear === endYear && iterMonth <= endMonth)) {
      if (!cards.find(c => c.month === iterMonth && c.year === iterYear)) {
        cards.push({
          id: '',
          month: iterMonth,
          year: iterYear,
          citizenId: citizenId,
          createdAt: new Date(iterYear, iterMonth, 1),
        })
      }
      iterMonth++
      if (iterMonth > 11) {
        iterMonth = 0
        iterYear++
      }
    }

    const lastDayOfMonthDate = new Date(endYear, endMonth + 1, 0).getDate()
    const todayDate = currentDateTime.getDate()
    const cardActivationDelay =
      globalSettings?.cardActivationDelay ??
      throwObjectNotFound(
        this.translate(ctx, d => d.globalSettings.cardActivationDelayNotSet),
        { uid: '<0bd2a3ca>' },
      )

    const diffDate = lastDayOfMonthDate - todayDate

    if (diffDate < cardActivationDelay) {
      if (iterMonth > 11) {
        iterMonth = 0
        iterYear++
      }

      if (!cards.find(c => c.month === iterMonth && c.year === iterYear)) {
        cards.push({
          id: '',
          month: iterMonth,
          year: iterYear,
          citizenId: citizenId,
          createdAt: new Date(iterYear, iterMonth, 1),
        })
      }
    }
    cards.sort((c1, c2) => {
      return c1.createdAt.getTime() > c2.createdAt.getTime() ? -1 : 1
    })

    return cards
  }

  async confirm(ctx: Context, card: ConfirmableDataForIdentityCard): Promise<IdentityCard> {
    const validateResult = await runWithROTransaction(this.opts.dbConnection, async tx => {
      let citizen: Citizen | undefined
      this.validateCitizenID(card.citizenId)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.IDENTITY_CARDS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_IDENTITY_CARDS_MANAGE)

        citizen = await this.checkForCitizenExists('<155fd335>', ctx, tx, card.citizenId)
        await this.checkAccessToCitizen('<6f31b281>', ctx, tx, citizen)
      }
      citizen ||= await this.checkForCitizenExists('<2ffa0af7>', ctx, tx, card.citizenId)

      const dbStoredCard = await this.opts.identityCardsRepository.findByDateAndCitizen(
        tx,
        this.validateMonth(card.month),
        this.validateYear(card.year),
        this.validateCitizenID(card.citizenId),
      )

      const cardAtivationDelay: number =
        (await this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID))?.cardActivationDelay ??
        throwObjectNotFound(
          this.translate(ctx, d => d.globalSettings.cardActivationDelayNotSet),
          { uid: '<5bb0e359>' },
        )

      this.validateCard(ctx, card as IdentityCard, cardAtivationDelay)

      return {
        dbStoredCard,
        cardIssuePointId: await this.requireCardIssuePointID(ctx, tx, card as IdentityCard),
        citizen,
      }
    })

    const { dbStoredCard, cardIssuePointId, citizen } = validateResult
    if (!citizen) throwInternalError('<04ac4003>')

    const confirmDateTime = this.opts.dateTimeProvider.get()

    if (dbStoredCard && dbStoredCard.confirmedAt) {
      // Если карточка уже выдана гражданину, то ничего не делаем
    } else if (dbStoredCard) {
      await this.update(ctx, dbStoredCard.id, {
        ...dbStoredCard,
        confirmedAt: confirmDateTime,
        municipalEntityId: citizen.municipalEntityId,
      })
    } else {
      await this.add(ctx, citizen.municipalEntityId, {
        ...card,
        confirmedAt: confirmDateTime,
        cardIssuePointId,
        municipalEntityId: citizen.municipalEntityId,
      })
    }

    return runWithROTransaction(this.opts.dbConnection, tx =>
      this.checkForIdentityCardExists('<6bf46f6e>', tx, card.month, card.year, card.citizenId),
    )
  }

  async print(ctx: Context, card: PrintableDataForIdentityCard): Promise<IdentityCard> {
    this.logger.debug(ctx, '<de5be7ee> Provide identity card for printing', { card })

    let cardToAdd: AddIdentityCardOpts | undefined
    let existingCard: IdentityCard | undefined

    const { citizen } = await runWithROTransaction(this.opts.dbConnection, async tx => {
      let citizen: Citizen | undefined
      this.validateCitizenID(card.citizenId)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.IDENTITY_CARDS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_IDENTITY_CARDS_MANAGE)

        citizen = await this.checkForCitizenExists('<15d6b4f9>', ctx, tx, card.citizenId)
        await this.checkAccessToCitizen('<bc302c07>', ctx, tx, citizen)
      }
      citizen ||= await this.checkForCitizenExists('<ffb39f87>', ctx, tx, card.citizenId)

      existingCard = await this.opts.identityCardsRepository.findByDateAndCitizen(
        tx,
        this.validateMonth(card.month),
        this.validateYear(card.year),
        this.validateCitizenID(card.citizenId),
      )

      const cardAtivationDelay: number =
        (await this.opts.globalSettingsRepository.load(tx, GLOBAL_SETTINGS_ID))?.cardActivationDelay ??
        throwObjectNotFound(
          this.translate(ctx, d => d.globalSettings.cardActivationDelayNotSet),
          { uid: '<0332b62a>' },
        )
      this.validateCard(ctx, card as IdentityCard, cardAtivationDelay)

      if (!existingCard) {
        cardToAdd = {
          ...card,
          confirmedAt: undefined,
          cardIssuePointId: await this.requireCardIssuePointID(ctx, tx, card as IdentityCard),
          municipalEntityId: citizen.municipalEntityId,
        }
      }
      return { citizen }
    })

    if (!citizen) throwInternalError('<e4d7a7bc>')

    if (cardToAdd) {
      await this.add(ctx, citizen.municipalEntityId, cardToAdd)
      existingCard = await runWithROTransaction(this.opts.dbConnection, tx =>
        this.checkForIdentityCardExists('<ae279fcd>', tx, card.month, card.year, card.citizenId),
      )
    }

    return existingCard ?? throwInternalError('<4036e0bd>')
  }

  private async add(ctx: Context, municipalEntityId: Id, opts: AddIdentityCardOpts): Promise<void> {
    const entityId = this.opts.idGenerator.generate()

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.IDENTITY_CARD_ADD,
        municipalEntityId: opts.municipalEntityId,
        payload: createObj<AddIdentityCardPayload>({
          month: opts.month,
          year: opts.year,
          cardIssuePointId: opts.cardIssuePointId,
          citizenId: opts.citizenId,
          confirmedAt: opts.confirmedAt?.toISOString(),
        }),
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  private async update(ctx: Context, entityId: Id, opts: UpdateIdentityCardOpts): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: entityId,
        type: EventType.IDENTITY_CARD_UPDATE,
        municipalEntityId: opts.municipalEntityId,
        payload: createObj<UpdateIdentityCardPayload>({
          confirmedAt: opts.confirmedAt?.toISOString(),
          cardIssuePointId: opts.cardIssuePointId,
        }),
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  private async checkAccessToCitizen(errorCode: string, ctx: Context, tx: ROTransaction, citizen: Citizen) {
    const userID = this.validateUserID(ctx.userId)
    const user =
      (await this.opts.usersRepository.findById(tx, userID)) ??
      throwObjectNotFound(`<2b8e276b> ${this.translate(ctx, d => d.users.notFoundByIdError, userID)}`)

    if (user.municipalEntityId !== citizen.municipalEntityId) {
      throwAccessDenied(
        `${errorCode} ${this.opts.translationService.translate(
          ctx.langCode,
          d => d.citizens.accessDenied,
          citizen.id,
        )}`,
      )
    }
  }

  private async checkForIdentityCardExists(
    errorCode: string,
    tx: ROTransaction,
    month: number,
    year: number,
    citizenId: string,
  ): Promise<IdentityCard> {
    return (
      (await this.opts.identityCardsRepository.findByDateAndCitizen(tx, month, year, citizenId)) ??
      throwObjectNotFound(
        `${errorCode} Идентификационной карты гражданина c id=${citizenId} для ${month}.${year} не существует`,
      )
    )
  }

  private async checkForCitizenExists(errorCode: string, ctx: Context, tx: ROTransaction, id: Id): Promise<Citizen> {
    return (
      (await this.opts.citizensRepository.findById(tx, id)) ??
      throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.citizens.notFoundByIdError, id)}`)
    )
  }

  private async requireCardIssuePointID(ctx: Context, tx: ROTransaction, card: IdentityCard): Promise<string> {
    const userID = this.validateUserID(ctx.userId)
    const user = await this.checkForUserExists('<c633e477>', ctx, tx, userID)

    const cardIssuePointID = validateRequiredId('<6a5fda4f> Card issue point ID', card.cardIssuePointId)
    const cardIssuePoint = await this.checkForCardIssuePointExists('<75387985>', ctx, tx, cardIssuePointID)

    if (user.cardIssuePointId) {
      if (user.cardIssuePointId === cardIssuePointID) return user.cardIssuePointId
      throwAccessDenied(
        this.translate(ctx, d => d.identityCards.noAccessToCardIssuePoint),
        { uid: '<ae3f5866>' },
      )
    } else {
      if (user.municipalEntityId) {
        if (cardIssuePoint.municipalEntityId === user.municipalEntityId) return cardIssuePointID
        throwAccessDenied(
          this.translate(ctx, d => d.identityCards.noAccessToCardIssuePoint),
          { uid: '<0f986fbc>' },
        )
      } else return cardIssuePointID
    }
  }

  private validateCard(ctx: Context, card: IdentityCard, cardActivationDelay: number) {
    const now = this.opts.dateTimeProvider.get()
    const curMonth = now.getMonth()
    const curYear = now.getFullYear()

    if (card.month === curMonth && card.year === curYear) return

    const lastDateOfCurMonth = new Date(curYear, curMonth + 1, 0).getDate()
    const curDate = now.getDate()

    const diff = lastDateOfCurMonth - curDate

    let nextMonth, nextYear
    if (curMonth >= 11) {
      nextMonth = 0
      nextYear = curYear + 1
    } else {
      nextMonth = curMonth + 1
      nextYear = curYear
    }

    if (diff < cardActivationDelay && card.month === nextMonth && card.year === nextYear) return

    throwInvalidParameter(
      this.translate(ctx, d => d.identityCards.completedOrLater),
      { uid: '<e9f7402e>' },
    )
  }

  private async checkForUserExists(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
    opts?: { withDeleted: boolean },
  ): Promise<User> {
    const user = opts?.withDeleted
      ? await this.opts.usersRepository.findById(tx, id)
      : await this.opts.usersRepository.findByIdNonDeleted(tx, id)

    return (
      user ??
      throwObjectNotFound(
        this.translate(ctx, d => d.users.notFoundByIdError, id),
        { uid: errorCode },
      )
    )
  }

  private async checkForCardIssuePointExists(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
    opts?: { withDeleted: boolean },
  ): Promise<CardIssuePoint> {
    const cardIssuePoint = opts?.withDeleted
      ? await this.opts.cardIssuePointsRepository.findById(tx, id)
      : await this.opts.cardIssuePointsRepository.findByIdNonDeleted(tx, id)

    return (
      cardIssuePoint ??
      throwObjectNotFound(
        this.translate(ctx, d => d.cardIssuePoints.notFoundByIdError, id),
        { uid: errorCode },
      )
    )
  }
}

export class IdentityCardsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      identityCardsRepository: IdentityCardsRepository
      citizensRepository: CitizensRepository
      eventCreator: ModelEventCreator
    },
  ) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const card =
      (await this.opts.identityCardsRepository.findById(tx, entityId)) ??
      throwObjectNotFound(
        trCore(d => d.cardIssuePoints.notFoundByIdError, entityId),
        { uid: '<4601236>' },
      )
    const citizen =
      (await this.opts.citizensRepository.findById(tx, card.citizenId)) ??
      throwObjectNotFound(
        trCore(d => d.citizens.notFoundByIdError, card.citizenId),
        { uid: '<3961abc0>' },
      )
    card.citizenId
    return this.opts.eventCreator.create(ctx, tx, {
      entityId: card.id,
      mode: CreateEventMode.UPDATE,
      type: EventType.IDENTITY_CARD_MERGE,
      municipalEntityId: citizen.municipalEntityId,
      payload: createObj<MergeIdentityCardPayload>({
        cardIssuePointId: card.cardIssuePointId,
        citizenId: card.citizenId,
        confirmedAt: card.confirmedAt?.toISOString(),
        createdAt: card.createdAt.toISOString(),
        month: card.month,
        year: card.year,
      }),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.IDENTITY_CARD_ADD: {
        const p = event.payload as AddIdentityCardPayload
        await this.opts.identityCardsRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          month: p.month,
          year: p.year,
          cardIssuePointId: p.cardIssuePointId,
          citizenId: p.citizenId,
          confirmedAt: p.confirmedAt ? new Date(p.confirmedAt) : undefined,
        })
        return true
      }
      case EventType.IDENTITY_CARD_UPDATE: {
        const p = event.payload as UpdateIdentityCardPayload
        const ic =
          (await this.opts.identityCardsRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(
            trCore(d => d.identityCards.notFoundByIdError, event.entityId),
            { uid: '<88ec628f>' },
          )
        await this.opts.identityCardsRepository.save(tx, {
          ...ic,
          cardIssuePointId: p.cardIssuePointId ?? ic.cardIssuePointId,
          confirmedAt: runIfDefined(p.confirmedAt, d => new Date(d)),
        })
        return true
      }
      case EventType.IDENTITY_CARD_MERGE: {
        const p = event.payload as MergeIdentityCardPayload
        await this.opts.identityCardsRepository.save(tx, {
          ...p,
          id: event.entityId,
          createdAt: new Date(p.createdAt),
          confirmedAt: runIfDefined(p.confirmedAt, v => new Date(v)),
        })
        return true
      }
    }
    return false
  }
}
